import { InteractionRequiredAuthError, BrowserAuthError, ServerError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from './msalConfig';

// Local dev: skip MSAL entirely. The dev API-gateway sim injects X-User-*
// headers regardless of the Authorization header, so the request goes through.
export const DEV_BYPASS_AUTH = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

// Guards against a redirect storm: if we kick off an interactive redirect and
// come back still unable to get a token, we must NOT redirect again (that loops
// forever, stuck on the #code= response URL). Time-bounded so a stale flag from
// a previous session can't permanently block all future redirects.
const REAUTH_FLAG = 'apn_reauth_inflight';
const REAUTH_FLAG_TTL_MS = 60_000;

export function clearReauthGuard() {
  try { sessionStorage.removeItem(REAUTH_FLAG); } catch { /* ignore */ }
}

function reauthGuardActive() {
  try {
    const raw = sessionStorage.getItem(REAUTH_FLAG);
    if (!raw) return false;
    const setAt = Number(raw);
    if (!Number.isFinite(setAt) || Date.now() - setAt > REAUTH_FLAG_TTL_MS) {
      sessionStorage.removeItem(REAUTH_FLAG);
      return false;
    }
    return true;
  } catch { return false; }
}

async function reauthenticate() {
  if (reauthGuardActive()) {
    // We already kicked off a redirect within the last minute and silent auth
    // still failed — stop, so we don't loop. Surface an error to the caller.
    clearReauthGuard();
    throw new Error('Sitzung konnte nicht erneuert werden. Bitte lade die Seite neu.');
  }
  sessionStorage.setItem(REAUTH_FLAG, String(Date.now()));
  // Use loginRedirect (not acquireTokenRedirect) when re-establishing a session:
  // it generates fresh PKCE and doesn't depend on prior cache state, which
  // matters when we got here because the cached refresh token is expired.
  await msalInstance.loginRedirect(loginRequest);
  // Parks until the browser navigates away, so callers never proceed.
  await new Promise(() => {});
}

// Recognize any silent-auth failure that genuinely requires interactive sign-in.
// MSAL inconsistently wraps refresh-token expiry: sometimes InteractionRequired-
// AuthError, sometimes ServerError with errorCode=invalid_grant (AADSTS70043,
// suberror=token_expired). We match all known shapes so the user actually gets
// redirected to log in instead of seeing a silent failure in the network tab.
function needsInteractiveSignIn(error) {
  if (error instanceof InteractionRequiredAuthError) return true;

  if (error instanceof BrowserAuthError) {
    // Silent renewal runs a prompt=none flow in a hidden iframe. When the cached
    // refresh token is expired AND the IdP session is gone, that iframe can't
    // complete and MSAL throws one of these — all mean "silent SSO is impossible,
    // send the user through an interactive login".
    // NOTE: 'timed_out' is the iframe-monitor timeout in msal-browser v3+, which
    // is what we actually hit on a day-old stale session (logs: "timed_out").
    // 'monitor_window_timeout' was its pre-v3 name — kept for resilience across
    // versions but it does NOT exist in the v5 we ship, so 'timed_out' is the one
    // that matters here.
    return error.errorCode === 'timed_out' ||
      error.errorCode === 'block_iframe_reload' ||
      error.errorCode === 'monitor_window_timeout' ||
      error.errorCode === 'hash_empty_error' ||
      error.errorCode === 'empty_navigate_uri' ||
      error.errorCode === 'no_account_error';
  }

  if (error instanceof ServerError) {
    return error.errorCode === 'invalid_grant' ||
      error.errorCode === 'interaction_required' ||
      error.errorCode === 'login_required' ||
      error.errorCode === 'consent_required';
  }

  // Final safety net: any error whose suberror flags an expired token.
  return error?.subError === 'token_expired' ||
    error?.errorCode === 'invalid_grant';
}

export async function getAccessToken() {
  if (DEV_BYPASS_AUTH) return '';

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    await reauthenticate();
    return '';
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: loginRequest.scopes,
    });
    clearReauthGuard(); // success — allow future re-auths
    return result.accessToken;
  } catch (error) {
    if (needsInteractiveSignIn(error)) {
      console.warn(
        'Silent token acquisition failed, re-authenticating:',
        error?.constructor?.name, error?.errorCode || error?.message,
        error?.subError ? `(sub: ${error.subError})` : ''
      );
      await reauthenticate();
      return '';
    }
    // Truly transient — surface to the caller (UI shows a retry).
    throw error;
  }
}

export async function authFetch(input, init = {}) {
  if (DEV_BYPASS_AUTH) {
    const headers = new Headers(init.headers);
    headers.set('X-User-Id', 'dev-user-001');
    headers.set('X-User-Email', 'info@ai-evora.com');
    headers.set('X-User-Name', 'Local Developer');
    return fetch(input, { ...init, headers });
  }

  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(input, { ...init, headers });

  // 401 from the API: token rejected (e.g. expired between acquisition and use).
  // Re-acquire once; getAccessToken redirects if the session is truly gone.
  if (response.status === 401) {
    const freshToken = await getAccessToken();
    headers.set('Authorization', `Bearer ${freshToken}`);
    return fetch(input, { ...init, headers });
  }
  return response;
}
